<template>
  <div>
    <StaticDataTable :class="['static-table-list', editing ? '' : 'stock-pricing-matrix-table']" v-if="bulkPricingData">
      <template v-slot:header>
        <tr>
          <th>Code</th>
          <th width="20%">{{editing ? 'Alias' : 'Name'}}</th>
          <template v-for="bulkContainer in bulkPricingData.stockContainerList">
            <th :key="bulkContainer.id">{{bulkContainer.code}}</th>
            <th :key="bulkContainer.id + '-landing'" v-if="!editing">Est. Landed</th>
          </template>                      
          <th>Actions</th>
        </tr>
      </template>
      <template v-slot:body>
        <tr v-for="(stock, stockIndex) in bulkPricingData.stockBulkPriceList" :key="stock.id">
          <td class="font-weight-bold">
            <LinkStock :id="stock.id" :name="stock.code" />
            <span v-if="stock.isPendingUpdate" class="pricing-pending-update">
              &nbsp;<Tooltip label="Price updates pending"><i class="far fa-clock"></i></Tooltip>
            </span>
          </td>
          <td>
            <span v-if="!editing" :class="stock.alias ? 'font-italic' : ''">{{stock.alias ? stock.alias : stock.name}}</span>
            <FormInputText 
              v-if="editing"
              :ref="`stock-alias-${stock.id}`"
              :value="stock.alias"
              @input="setStockAlias(stock, $event)"
              placeholder="Stock Alias"
            />
          </td>
          <template v-for="(bulkContainer, index) in bulkPricingData.stockContainerList">
            <td :key="bulkContainer.id + '-' + stock.id" :class="!(index & 1) ? 'table-data-alternate-column' : ''">
              <template v-if="!editing || stock.isPendingUpdate">{{getContainerPrice(stock, bulkContainer.id)}}</template>
              <span v-show="editing && stock.isPendingUpdate" class="form-field-note">Price update pending, cannot update</span>
              <FormInputCurrency 
                v-if="editing && !stock.isPendingUpdate"
                :value="getContainerPriceValue(stock, bulkContainer.id)"
                @input="setContainerPriceValue(stock, bulkContainer.id, $event)"
                @tab="onPriceTabbed($event, stockIndex, index)"
              />
            </td>
            <td :key="bulkContainer.id + '-' + stock.id + '-landing'" :class="['font-weight-bold', !(index & 1) ? 'table-data-alternate-column' : '']" v-if="!editing">{{getContainerLanding(stock, bulkContainer.id)}}</td>
          </template>
          <td>
            <a @click.prevent="removeEditingGlassPricing(stock.id)" v-if="editing"><i class="far fa-trash-alt"></i></a>
            <ActionDropMenu :tableAction="true" :forceDropdown="true" v-if="!editing && stock.isPendingUpdate && userHasReadPermission(PermissionTypes.PricingPermissionTypes.pricingApproval)">
              <DropdownMenuItem :link="`/pricing/requests/${stock.stockPriceRequestGroupId}`" icon="fa-clock" label="Show Pending Prices">Show Pending Prices</DropdownMenuItem>
            </ActionDropMenu>
          </td>
        </tr>

        <tr v-if="editing && bulkPricingData.stockContainerList && addingNewStock">
          <td :colspan="2">
            <div class="alert alert-warning" v-if="editingError">
              <i class="far fa-exclamation-triangle"></i>&nbsp;&nbsp;<strong>Error:</strong> {{editingError}}
            </div>
            <FormSearchableSelect ref="newStockInput" :options="lookupStockList" v-model="newStockId" @input="newStockSelected" placeholder="Stock Code" :loading="loading" v-if="addingNewStock" />
          </td>
          <td :colspan="bulkPricingData.stockContainerList.length + 1">
            <ActionButton type="cancel" class="btn-xs ml-2" @click="cancelAddingStockItem()"> Cancel</ActionButton>
          </td>
        </tr>
        <tr v-if="editing && bulkPricingData.stockContainerList && !addingNewStock">
          <td :colspan="bulkPricingData.stockContainerList.length + 3">
            <ActionButton type="edit" class="btn-xs" @click="newStockButtonPressed" v-if="!addingNewStock"> New Stock</ActionButton>
          </td>
        </tr>
      </template>
    </StaticDataTable>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapState } from 'vuex'
import StaticDataTable from '@/components/table/staticDataTable'
import { InventoryStockTypes, StockPriceTypes } from '@/store/types'

export default {
  name: 'SupplierBulkPriceList',
  components: {
    StaticDataTable
  },
  props: {
    contactId: Number,
    supplierCurrencyCode: String,
    supplierBulkPricingData: Object,
    editing: Boolean,
  },
  data() {
    return {
      loading: false,
      stockList: [],
      lookupStockList: [],
      addingNewStock: false,
      newStockId: null,
      bulkPricingData: _.cloneDeep(this.$props.supplierBulkPricingData),
      editingError: null
    }
  },
  watch: {
    supplierBulkPricingData: function() {
      this.$data.bulkPricingData = _.cloneDeep(this.$props.supplierBulkPricingData)
    }
  },
  computed: {
    ...mapState({
      accountCurrencyCode: state => state.account.currencyCode,
    })
  },
  mounted() {
    this.loadStockList()
  },
  methods: {
    getContainerPriceValue(stock, containerId) {
      var priceInfo = stock.prices.filter(price => price.stockContainerId == containerId)[0]
      if(priceInfo == null)
        return undefined
      return priceInfo.price
    },
    setContainerPriceValue(stock, containerId, value) {
      var priceInfo = stock.prices.filter(price => price.stockContainerId == containerId)[0]
      if(priceInfo == null) {
        if(value != '' && value != null) {
          stock.prices.push({
            price: value,
           stockContainerId: containerId,
            landedCost: 0
          })
        }
      } else {
        priceInfo.price = value
      }
      this.$emit('update', this.$data.bulkPricingData)
    },
    getContainerPrice(stock, containerId) {
      var value = this.getContainerPriceValue(stock, containerId)
      if(value === undefined)
        return '-'
      return this.$options.filters.formatCurrency(value) + ' ' + (this.supplierCurrencyCode || '')
    },
    getContainerLanding(stock, containerId) {
      var priceInfo = stock.prices.filter(price => price.stockContainerId == containerId)[0]
      if(priceInfo == null)
        return '-'
      return this.$options.filters.formatCurrency(priceInfo.landedCost) + ' ' + this.accountCurrencyCode
    },
    setStockAlias(stock, alias) {
      stock.alias = alias
      this.$emit('update', this.$data.bulkPricingData)
    },

    loadStockList() {
      this.loading = true
      this.$apiService.inventoryService.getStockList().then(response => {
        this.$data.stockList = response.data

        this.$data.lookupStockList = this.$data.stockList
          .filter(stock => stock.isActive && stock.type == InventoryStockTypes.glass)
          .map(stock => {
            return {
              value: stock.id,
              name: `${stock.code} | ${stock.name}`
            }
          })
      })
      .finally(() => {
        this.loading = false
      })
    },
    newStockButtonPressed() {
      this.addingNewStock = true
      this.editingError = ''
      this.$nextTick(() => {
        this.$refs.newStockInput.focus()
        this.$refs.newStockInput.select()
      })
    },
    cancelAddingStockItem() {
      this.addingNewStock = false
      this.editingError = ''
      this.newStockId = null
    },
    newStockSelected(stockId) {
      if(this.addNewStockItem(stockId)) {
        this.$nextTick(() => {
          let aliasRef = `stock-alias-${stockId}`
          this.$refs[aliasRef][0].focus()
          this.$refs[aliasRef][0].select()
        })
      }
    },
    addNewStockItem(stockId) {
      this.editingError = null
      if(stockId == null)
        return false

      // Check if the code is already in the list
      if(this.bulkPricingData.stockBulkPriceList.find(stock => stock.id == stockId) != null) {
        this.editingError = 'Stock item is already in the list'
        return false
      }

      // Get stock info from list
      var stockItem = this.$data.stockList.find(stock => stock.id == stockId)
      if(stockItem == null) {
        this.editingError = 'Could not find stock item'
        return false
      }

      // Add an item to the list
      this.bulkPricingData.stockBulkPriceList.push({
        id: stockId,
        code: stockItem.code,
        name: stockItem.name,
        prices: []
      })

      this.$emit('update', this.$data.bulkPricingData)

      this.addingNewStock = false
      this.newStockId = null

      return true
    },
    removeEditingGlassPricing(stockId) {
      this.bulkPricingData.stockBulkPriceList = this.bulkPricingData.stockBulkPriceList.filter(stock => stock.id != stockId)
      this.$emit('update', this.$data.bulkPricingData)
    },
    onPriceTabbed(evt, stockIndex, containerIndex) {
      // If the last container & stock item then start a new stock row
      if(stockIndex >= this.bulkPricingData.stockBulkPriceList.length-1 && containerIndex >= this.bulkPricingData.stockContainerList.length-1) {
        evt.preventDefault()
        this.newStockButtonPressed()
      }
    }
  }  
}
</script>

